import React, { useMemo, useCallback, memo } from 'react'
import { useAppSelector } from '@store/hooks'
import { IDentalChart } from '@dataTypes/chartingFeature.types'
import {
  ITreatmentPlan,
  IPhaseItems,
  IPhases,
} from '@dataTypes/treatmentPlan.types'
import ToothChart from '../../Dental/ToothChart.component'
import {
  getTreatmentPlanReferencedToothIds,
  mergeChartDentalDataWithTreatmentPlanItems,
} from '@utils/treatment-plan.utils'

export type TreatmentPlanToothChartLayoutMode = 'full' | 'xray-split'

interface TreatmentPlanToothChartProps {
  activeTreatmentPlanId?: string | null
  phaseVisibility?: Record<string, boolean>
  formStatePhases?: IPhases[] | null
  /** `xray-split`: chart sits in left pane of x-ray split; `full`: accordion full width. */
  layoutMode?: TreatmentPlanToothChartLayoutMode
  /** When true, chart fills a fixed-height pane (manual vertical resize). */
  fillPane?: boolean
  interactive?: boolean
  selectedTeeth?: number[]
  onToggleTooth?: (toothId: number) => void
}

const TreatmentPlanToothChart = ({
  activeTreatmentPlanId,
  phaseVisibility = {},
  formStatePhases = null,
  layoutMode = 'full',
  fillPane = false,
  interactive = false,
  selectedTeeth = [],
  onToggleTooth,
}: TreatmentPlanToothChartProps): React.ReactElement => {
  const dentalCharts = useAppSelector((state) => state.charting.dentalCharts)
  const chartingLoading = useAppSelector((state) => state.charting.loading)
  const treatmentPlans = useAppSelector(
    (state) => state.charting.treatmentPlans,
  )
  const noopSetSelectedTooth = useCallback(
    (_next: React.SetStateAction<number | null>): void => {
      void _next
    },
    [],
  )
  const selectedTeethSet = useMemo(
    (): ReadonlySet<number> => new Set(selectedTeeth),
    [selectedTeeth],
  )
  const latestDentalChart: IDentalChart | null = useMemo(() => {
    return dentalCharts.length > 0 ? dentalCharts[0] : null
  }, [dentalCharts])
  const activeTreatmentPlan: ITreatmentPlan | null = useMemo(() => {
    if (!activeTreatmentPlanId) {
      return null
    }
    return (
      treatmentPlans.find((plan) => plan.tp_id === activeTreatmentPlanId) ||
      null
    )
  }, [treatmentPlans, activeTreatmentPlanId])
  const treatmentPlanItems: IPhaseItems[] = useMemo(() => {
    const phasesToUse = formStatePhases || activeTreatmentPlan?.phases || []
    if (phasesToUse.length === 0) {
      return []
    }
    return phasesToUse
      .filter((phase) => {
        if (phase.is_hidden) {
          return false
        }
        const phaseId = phase.phase_id || ''
        return phaseVisibility[phaseId] !== false
      })
      .flatMap((phase) => phase.items.filter((item) => !item.is_hidden))
  }, [activeTreatmentPlan, phaseVisibility, formStatePhases])
  const mergedDentalData = useMemo(() => {
    return mergeChartDentalDataWithTreatmentPlanItems(
      latestDentalChart?.dental_data ?? [],
      treatmentPlanItems,
    )
  }, [latestDentalChart, treatmentPlanItems])
  const treatmentPlanHighlightToothIds = useMemo(() => {
    return getTreatmentPlanReferencedToothIds(treatmentPlanItems)
  }, [treatmentPlanItems])

  const isHeightConstrained = layoutMode === 'xray-split' || fillPane
  const innerClassName = [
    'charting-chart-content',
    'treatment-plan-tooth-chart-inner',
    `treatment-plan-tooth-chart-inner--${layoutMode}`,
    'w-100',
    'min-h-0',
    'd-flex',
    'flex-column',
    isHeightConstrained ? 'h-100' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={innerClassName}>
      <div
        className={`treatment-plan-chart-scale-host w-100${isHeightConstrained ? ' h-100' : ''}`}
      >
        <div
          className={`treatment-plan-chart-container box-shadow-0 border-0 d-flex flex-column min-h-0${isHeightConstrained ? ' h-100' : ''}`}
        >
          <ToothChart
            dentalData={mergedDentalData}
            selectedTooth={null}
            setSelectedTooth={noopSetSelectedTooth}
            view="full"
            isDataLoading={chartingLoading}
            treatmentPlanHighlightToothIds={treatmentPlanHighlightToothIds}
            readOnly={!interactive}
            allowUpscale={isHeightConstrained}
            multiSelect={interactive}
            selectedTeeth={interactive ? selectedTeethSet : undefined}
            onToggleTooth={interactive ? onToggleTooth : undefined}
          />
        </div>
      </div>
    </div>
  )
}

export default memo(TreatmentPlanToothChart)
